import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, take } from 'rxjs';

export abstract class AbstractEntityTabLoader<T extends { id: string }> {
  public entities$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  protected cache = new Map<string, Observable<T[]>>();
  protected loading = false; // prevent multiple loads

  protected page = 1;
  protected searchTerm: string | null = null;

  protected abstract pageSize: number;
  protected abstract sort?: string;
  protected abstract searchFilterKey: string;

  constructor(
    protected store: Store,
    protected service: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ensureLoaded(query: any): Promise<void>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      allByQuery(query: any): any
    }
  ) {}

  async loadInitial(): Promise<BehaviorSubject<T[]>> {
    this.page = 1;
    this.searchTerm = null;
    this.entities$.next([]); // clear old items
    return this.load();
  }

  async search(term: string): Promise<BehaviorSubject<T[]>> {
    this.page = 1;
    this.searchTerm = term?.trim() || null;
    this.entities$.next([]); // clear old search results
    return this.load();
  }

  async loadMore(): Promise<BehaviorSubject<T[]>> {
    if (this.loading) return this.entities$;
    if (this.searchTerm) return this.entities$; // don't paginate search

    this.page++;
    return this.load();
  }

  protected async load(): Promise<BehaviorSubject<T[]>> {
    if (this.loading) return this.entities$;
    this.loading = true;

    const query = this.buildQuery();

    await this.service.ensureLoaded(query);

    const newItems$ = this.store.select(this.service.allByQuery(query)) as Observable<T[]>;

    // append new items to current BehaviorSubject
    newItems$.pipe(take(1)).subscribe(newItems => {
      const current = this.entities$.getValue();
      const combined = [...current, ...newItems].filter(
        (item, index, self) => index === self.findIndex(s => s.id === item.id)
      );
      this.entities$.next(combined);
    });

    this.loading = false;
    return this.entities$;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected buildQuery(): Record<string, any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const q: Record<string, any> = {
      'page[number]': this.page,
      'page[size]': this.pageSize,
    };
    if (this.sort) q.sort = this.sort;
    if (this.searchTerm) q[this.searchFilterKey] = this.searchTerm;
    return q;
  }
}
